import { useState, useRef } from 'react';
import type { UseKPITrendsProps } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import { fetchComparePCConsoleMetricData } from '@intelligence/services/compare';
import { allMetricsOptions } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import {
  getDeviceConfig,
  findFirstMatchingElement,
} from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import {
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
  TopchartPageId,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import { toDateTypeFilter } from '@/pages/intelligence/utils/common';
import { componentDefault } from '@intelligence/components/Filter/FilterBase/FilterList';
import { GRANULARITY_MOMENT_HELPER_MAPPING } from '@intelligence/utils/dateUtils';

// useSameStartDate获取数据与接口交互的hooks
const useSameStartDate = (props: UseKPITrendsProps) => {
  const {
    entityType = PC_CONSOLE_COMBINE_ENTITY_TYPE,
    filterValue,
    setFilterValue,
  } = props || {};
  // console.log('useSameStartDate', props);
  const deviceDefVales = getDeviceConfig(
    TopchartPageId.PCConsoleMetricsTable,
    entityType,
  )?.value;
  const [currentMetric, setCurrentMetric] = useState<string>('');
  const [fetchParams, setFetchParams] = useState({
    ids: [],
    entity_type: entityType,
    markets: ['global'],
    granularity: 'monthly',
    start_date: '',
    end_date: '',
    platforms: deviceDefVales,
    source_metrics: {},
    segments: ['All'],
    start_release_time: '',
    end_release_time: '',
    b_group_by_date: false,
    b_group_by_market: false,
    b_group_by_region: false,
    b_group_by_platform: false,
    b_group_by_segment: false,
    b_growth_ratio: true,
  } as any);

  // 接口获取对比游戏各指标数据
  const [metricValueGameList, setMetricValueGameList] = useState<any>([]);
  // 接口获取对比游戏各指标数据-可视化折线图数据
  const [metricListGameLineData, setMetricListGameLineData] = useState<any>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  // 可视化区域loading
  const [visualisLoading, setVisualisLoading] = useState(false);
  // 返回结果的查询时间，用于空数据补全
  const [responseQueryDate, setResponseQueryDate] = useState<{
    startDate: string | number;
    endDate: string | number;
  }>();

  const abortControllerRef = useRef<AbortController>();

  // 获取ComparePCConsoleMetricData数据
  const getIndexPageData = async (
    params: any,
    filterValueData: any = filterValue,
  ) => {
    const newFetchParams = { ...fetchParams, ...params };
    setFetchParams(newFetchParams);
    const { source_metrics: sourceMetrics } = newFetchParams;
    const objValueList: any[] = Object.values(sourceMetrics).flat();

    // setCurrentMetric(objValue[0]);
    if (
      isEmpty(newFetchParams?.ids) ||
      isEmpty(sourceMetrics) ||
      isEmpty(newFetchParams?.granularity)
    ) {
      setFetchLoading(false);
      return;
    }
    setFetchLoading(true);
    try {
      // 当前请求发起时将之前请求取消
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      setTimeout(() => setFetchLoading(true));

      getComparePCConsoleMetricLineData(newFetchParams);
      const trendRes = await fetchComparePCConsoleMetricData(newFetchParams, {
        signal: abortControllerRef.current?.signal,
      });
      const { data = [], req = {} } = trendRes || {};

      // 为了与宽表表头顺序保持一致
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { source_metrics = {} } = newFetchParams;
      const objMetricValue = Object.values(source_metrics).flat();
      const filterMetrics = allMetricsOptions.filter((item: any) => {
        return objMetricValue.includes(item?.value);
      });
      const filterMetricsValueListTemp = filterMetrics?.map(
        (item: any) => item.value,
      );
      const filterMetricsValueList =
        Array.isArray(filterMetricsValueListTemp) &&
        filterMetricsValueListTemp?.length > 0
          ? filterMetricsValueListTemp
          : objValueList;
      // 获取有数据的最前面的一个指标，给默认排序
      const defaultSortMetric = findFirstMatchingElement(
        data,
        filterMetricsValueList,
      );
      setCurrentMetric(defaultSortMetric || filterMetricsValueList[0]);

      setMetricValueGameList(data);
      const { granularity, start_date: startDate = '', end_date: endDate = '' } = req;
      const granularityMomentHelper = GRANULARITY_MOMENT_HELPER_MAPPING[granularity] || GRANULARITY_MOMENT_HELPER_MAPPING.monthly;
      const typeTemp = toDateTypeFilter?.[req?.granularity];
      let timeValue = componentDefault?.[typeTemp];
      if (startDate && startDate !== '') {
        timeValue = [startDate, endDate];
      }
      setFilterValue({
        ...filterValueData,
        rangeTime: {
          ...filterValueData?.rangeTime,
          value: timeValue,
        },
      });
      setFetchParams({
        ...newFetchParams,
        granularity,
        date_type: granularityMomentHelper.dateType,
        start_date: startDate,
        end_date: endDate,
      });
      // getComparePCConsoleOpinionMetricLineData();
    } catch {
      setMetricValueGameList([]);
      setCurrentMetric(objValueList[0]);
    } finally {
      setFetchLoading(false);
    }
  };

  // 获取ComparePCConsoleMetricData数据-Line
  const getComparePCConsoleMetricLineData = async (params: any) => {
    setVisualisLoading(true);
    const newLineParams = { ...params, b_group_by_date: true };
    try {
      const trendRes = await fetchComparePCConsoleMetricData(newLineParams);
      const { data = [], req = {} } = trendRes || {};

      // 返回的查询到的 AlignToRelease 日期数
      const responseReleaseDate = req.start_release_time &&
        req.end_release_time && {
          startDate: req.start_release_time,
          endDate: req.end_release_time,
        };

      // 使用 AlignToRelease 相对时间数，或查询到的绝对时间
      setResponseQueryDate(
        responseReleaseDate || {
          startDate: req.start_date,
          endDate: req.end_date,
        },
      );

      setMetricListGameLineData(data);
    } catch {
      setMetricListGameLineData([]);
    } finally {
      setVisualisLoading(false);
    }
  };

  // 获取ComparePCConsoleMetricData数据-Line
  // const getComparePCConsoleOpinionMetricLineData = async () => {
  //   const newLineParams = {
  //     start_time: '2024-10-13 00:00:00',
  //     end_time: '2024-11-12 23:59:59',
  //     edition_unified_id: 'e7f672beaa5fddd166df98bc046ba4bd4',
  //     entity_type: 'pc',
  //     games: [
  //       {
  //         edition_unified_id: 'e7f672beaa5fddd166df98bc046ba4bd4',
  //         entity_type: 'pc',
  //         id_type: 'edition_id',
  //         release_time: '2024-12-12 00:00:00',
  //       },
  //       {
  //         edition_unified_id: 'e91eeea272398097f28c93d0a49f58379',
  //         entity_type: 'pc',
  //         id_type: 'edition_id',
  //         release_time: '2024-12-12 00:00:00',
  //       },
  //     ],
  //     metrics: [
  //       'Mentions',
  //       'Brand Health',
  //       'Steam All reviews',
  //       'Steam All reviews Positive%',
  //       'Steam Filtered Reviews',
  //       'Steam Filtered Positive%',
  //     ],
  //   };
  //   try {
  //     const trendRes = await fetchComparePCConsoleOpinionMetricData(newLineParams);
  //     console.log('trendRestrendRes', trendRes);
  //   } catch {
  //   } finally {
  //   }
  // };

  return {
    fetchParams,
    metricValueGameList,
    fetchLoading,
    metricListGameLineData,
    getIndexPageData,
    visualisLoading,
    currentMetric,
    responseQueryDate,
  };
};

export default useSameStartDate;
